import api from '@/api'
import client from '@/api/client'
import endpoints from '@/api/endpoints'
import { has, isNumber, isNull, includes } from 'lodash'
import qs from 'qs'

const defaultState = () => ({
  page: 1,
  totalAssets: 0,
  assets: [],
  filters: {
    brandIds: [],
    keyword: '',
    assetSources: ['diffbot', 'nativo_advertiser'],
    distribution: 'all',
    language: '',
    domain: '',
    metricDateStart: '-10000',
    metricDateEnd: '0',
    createdDateStart: '-10000',
    createdDateEnd: '0',
    wordCount: [],
    customProperties: [],
    topics: [],
    keywords: [],
    collection: ''
  },
  sort: {
    value: 'created_at',
    order: 'desc'
  },
  dynamicOptions: {
    domains: [],
    languages: [],
    topics: [],
    keywords: [],
    properties: []
  },
  optionLoading: {
    topics: false,
    keywords: false,
    properties: false
  }
})

const mutations = {
  setAssets (state, data) { state.assets = data },
  addToAssets (state, data) { state.assets = state.assets.concat(data) },
  setTotalAssets (state, data) { state.totalAssets = data },
  setSort (state, { key, value }) { state.sort[key] = value },
  setFilter (state, { key, value }) { state.filters[key] = value },
  setPage (state, value) { state.page = value },
  setFiltersFromObject (state, obj) {
    Object.keys(obj)
      .forEach(key => {
        if (has(state.filters, key)) {
          if (state.filters[key] && state.filters[key].constructor === Object) {
            state.filters[key] = Object.assign({}, state.filters[key], obj[key])
          } else {
            state.filters[key] = obj[key]
          }
        }
      })
  },
  setSortFromObject (state, obj) {
    Object.keys(obj)
      .forEach(key => {
        if (has(state.sort, key)) {
          state.sort[key] = obj[key]
        }
      })
  },
  setDynamicOption (state, { key, value }) { state.dynamicOptions[key] = value.map(o => Object.freeze(o)) },
  setLoading (state, { key, value }) { state.optionLoading[key] = value },
  resetStateToDefault (state) {
    Object.assign(state, defaultState())
  }
}

const actions = {
  getAssets ({ commit }, params) {
    return client.searchCreate(params)
      .then(resp => {
        commit('setAssets', resp.data.results)
        commit('setTotalAssets', resp.data.result_count)
      })
  },
  getMoreAssets ({ commit }, params) {
    return client.searchCreate(params)
      .then(resp => {
        commit('addToAssets', resp.data.results)
        commit('setTotalAssets', resp.data.result_count)
      })
  },
  resetFilters ({ commit }, brandId) {
    return new Promise((resolve, reject) => {
      const defaults = defaultState()
      try {
        commit('setFiltersFromObject', Object.assign({}, defaults.filters, { brandIds: [String(brandId)] }))
        commit('setSortFromObject', defaults.sort)
        resolve(true)
      } catch (err) {
        reject(err)
      }
    })
  },
  updateBrowserUrl (_context, params) {
    return new Promise((resolve) => {
      const newQS = qs.stringify(params, { strictNullHandling: true })
      const {
        protocol,
        host,
        pathname
      } = window.location
      const newUrl = `${protocol}//${host}${pathname}?${newQS}`
      window.history.replaceState({}, '', newUrl)
      resolve(true)
    })
  },
  loadDomainOptions ({ commit }, brandId) {
    return api.get(endpoints.brands.hostNames(brandId))
      .then(({ data }) => {
        const preparedData = data
          .map(({ value }) => ({ label: value, value }))
        commit('setDynamicOption', { key: 'domains', value: preparedData })
      })
  },
  loadLanguageOptions ({ commit }, brandId) {
    return api.get(endpoints.brands.languages(brandId))
      .then(({ data }) => {
        const preparedData = data
          .map(item => ({ label: item[0], value: item[1] }))
        commit('setDynamicOption', { key: 'languages', value: preparedData })
      })
  },
  loadTopicOptions ({ commit }, params) {
    commit('setLoading', { key: 'topics', value: true })
    const preparedParams = Object.assign({}, params, {
      source: 'iab'
    })
    return api.get(endpoints.assets.listTopics(), { params: preparedParams })
      .then(({ data }) => {
        const options = data.results
          .filter(category => isNumber(category.id) && category.subject !== '')
          .map(category => ({
            label: category.subject,
            value: category.subject,
            level: category.level,
            parent: category.parent_subject,
            assetCount: category.asset_count
          }))
        commit('setDynamicOption', { key: 'topics', value: options })
      })
      .then(() => { commit('setLoading', { key: 'topics', value: false }) })
  },
  loadKeywordOptions ({ commit }, params) {
    commit('setLoading', { key: 'keywords', value: true })
    return api.get(endpoints.keywords.list(), { params })
      .then(resp => {
        const options = resp.data.results
          .map(kw => ({
            label: kw.keyword,
            rightLabel: kw.assetCount,
            value: kw.keyword
          }))
        commit('setDynamicOption', { key: 'keywords', value: options })
      })
      .then(() => { commit('setLoading', { key: 'keywords', value: false }) })
  },
  loadCustomPropertyOptions ({ commit }, params) {
    commit('setLoading', { key: 'properties', value: true })

    const preparedParams = Object.assign({}, params, { return_values: true })

    return api.get(endpoints.properties.list(), { params: preparedParams })
      .then(({ data }) => {
        const preparedData = data.map(item => {
          const preparedValues = item.values.map(v => ({
            label: v.value,
            value: `${item.name}__${v.value}`,
            rightLabel: v.assetCount
          }))

          return {
            label: item.name,
            value: item.name,
            children: preparedValues,
            clickable: false
          }
        })

        commit('setDynamicOption', { key: 'properties', value: preparedData })
      })
      .then(() => { commit('setLoading', { key: 'properties', value: false }) })
  },
  createCollection (_context, payload) {
    return api.post(endpoints.collections.list(), payload)
  },
  updateCollection (_context, { id, payload }) {
    return api.patch(endpoints.collections.detail(id), payload)
  },
}


const topicToTreeNode = (t, topics) => ({
  value: t.label,
  label: t.value,
  rightLabel: t.assetCount,
  children: topics
    .filter(t2 => t2.parent === t.label)
    .map(t2 => topicToTreeNode(t2, topics))
})


const getters = {
  searchQueryParams: state => ({
    phrase: state.filters.keyword.length ? state.filters.keyword : null,
    brand_ids: state.filters.brandIds,
    order_by: state.sort.value,
    order_direction: state.sort.order,
    page: state.page,
    query_metrics: state.sort.value !== 'created_at' ? [state.sort.value] : [],
    asset_metrics: state.sort.value !== 'created_at' ? [state.sort.value] : [],
    human_languages: state.filters.language.length ? [state.filters.language] : null,
    data_providers: state.filters.assetSources,
    distribution: state.filters.distribution,
    domains: state.filters.domain.length ? [state.filters.domain] : null,
    metric_date_range: {
      gte: state.filters.metricDateStart,
      lte: state.filters.metricDateEnd
    },
    created_date_range: {
      gte: state.filters.createdDateStart,
      lte: state.filters.createdDateEnd
    },
    collection: state.filters.collection && state.filters.collection.length ? state.filters.collection : null,
    properties: state.filters.customProperties &&
      state.filters.customProperties.length
        ? state.filters.customProperties
        : null,
    topics: state.filters.topics && state.filters.topics.length
      ? state.filters.topics
      : null,
    keywords: state.filters.keywords && state.filters.keywords.length
      ? state.filters.keywords
      : null
  }),
  browserQueryParams: ({ filters, sort }) => Object.assign({}, filters, { sort }),
  topicOptionsAsTree: ({ dynamicOptions: { topics } }) => {
    let preppedTopics = topics
      .filter(t => isNull(t.parent))
      .map(t => topicToTreeNode(t, topics))

    if (preppedTopics.length < 1) {
      preppedTopics = topics
        .map(t => topicToTreeNode(t, topics))
    }

    return preppedTopics
  },
  topicOptionsSearch: ({ dynamicOptions: { topics } }) => term => {
    return topics
      .filter(t => includes(t.label.toLowerCase(), term.toLowerCase()))
      .map(t => topicToTreeNode(t, topics))
  },
  optionLoading: state => optionType => state.optionLoading[optionType],
  defaultFilter: () => key => defaultState().filters[key]
}

export default {
  state: defaultState(),
  mutations,
  actions,
  getters,
  namespaced: true
}
